/**
 * V13.17 — Formulaire public de référencement d'activité (sans auth).
 *
 * Route publique : /proposer-mon-activite?type=sante|boutique|services
 *
 * La fiche est enregistrée en `submission_status=pending` + `published=false`.
 * Un email de confirmation est envoyé (best-effort via NotificationService).
 * L'Admin/COPF valide ensuite depuis /admin/directory?tab=pending.
 *
 * Anti-spam : honeypot `website_confirm` + rate-limit backend (3/email/24h, 5/IP/1h).
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { HeartPulse, ShoppingBag, Briefcase, CheckCircle2, Loader2, Info, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { showError } from "@/lib/errors";

const TYPE_META = {
  sante: { label: "Santé & Performance", Icon: HeartPulse, tone: "hsl(var(--aito-lagoon))" },
  boutique: { label: "Boutiques & Services", Icon: ShoppingBag, tone: "hsl(var(--aito-gold))" },
  services: { label: "Services aux sportifs", Icon: Briefcase, tone: "hsl(var(--aito-energy))" },
};

export default function DirectorySubmitPage() {
  const [params] = useSearchParams();
  const initialType = TYPE_META[params.get("type")] ? params.get("type") : "sante";
  const [taxonomy, setTaxonomy] = useState(null);
  const [type, setType] = useState(initialType);
  const [category, setCategory] = useState("");
  const [form, setForm] = useState({
    name: "", contact_email: "", description: "",
    short_motto: "", island: "", commune: "", address: "",
    contact_phone: "", website: "",
    social_facebook: "", social_instagram: "", social_tiktok: "",
    website_confirm: "",  // honeypot
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/directory/taxonomy").then((r) => setTaxonomy(r.data)).catch(() => setTaxonomy(null));
  }, []);

  useEffect(() => { setCategory(""); }, [type]);

  const categories = (taxonomy?.[type]?.categories || []);
  const meta = TYPE_META[type];
  const Icon = meta.Icon;

  const isValid = () =>
    form.name.trim().length >= 2 &&
    /.+@.+\..+/.test(form.contact_email.trim()) &&
    form.description.trim().length >= 10 &&
    !!category;

  // V13.20 — Diagnostic dynamique des champs manquants (guide l'utilisateur).
  const missingFields = (() => {
    const missing = [];
    if (!category) missing.push("Catégorie");
    if (form.name.trim().length < 2) missing.push("Nom de l'activité");
    if (!/.+@.+\..+/.test(form.contact_email.trim())) missing.push("Email valide");
    if (form.description.trim().length < 10) missing.push("Description (≥ 10 caractères)");
    return missing;
  })();
  const canSubmit = missingFields.length === 0;

  const submit = async () => {
    if (!canSubmit) {
      toast.error(
        "Champs obligatoires manquants : " + missingFields.join(", ")
      );
      return;
    }
    setSubmitting(true);
    try {
      const payload = { type, category, ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await api.post("/directory/submit", payload);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      showError(e, { prefix: "Référencement :" });
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto p-4 md:p-6" data-testid="directory-submit-done">
        <Card className="p-8 text-center border-l-4 border-emerald-500">
          <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500 mb-3" />
          <h1 className="font-display text-2xl font-bold mb-2">Merci pour votre demande !</h1>
          <p className="text-sm text-muted-foreground">
            Votre demande a bien été transmise à AITO.PF pour validation.
            Vous recevrez un email à <strong>{form.contact_email}</strong> dès que votre fiche sera publiée.
          </p>
          <div className="flex flex-wrap gap-2 justify-center mt-6">
            <Link to="/"><Button variant="outline">Retour à l'accueil</Button></Link>
            <Link to={`/annuaire/${type === "sante" ? "sante-performance" : type === "boutique" ? "boutiques-services" : "services"}`}>
              <Button className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]">
                Voir l'annuaire {meta.label}
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-4 md:p-6 space-y-4" data-testid="directory-submit-page">
      <Link to="/annuaire">
        <Button variant="ghost" size="sm"><ArrowLeft className="h-4 w-4 mr-1" />Retour à l'annuaire</Button>
      </Link>

      <div className="flex items-start gap-3">
        <div className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${meta.tone}15`, border: `1px solid ${meta.tone}40` }}>
          <Icon className="h-6 w-6" style={{ color: meta.tone }} />
        </div>
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold">Référencer mon activité</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Formulaire ouvert à tous les professionnels. Votre demande sera validée par l'équipe AITO.PF avant publication.
          </p>
        </div>
      </div>

      <Card className="p-3 bg-blue-50/60 border-blue-200 flex items-start gap-2 text-xs" data-testid="directory-submit-info">
        <Info className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
        <div className="text-blue-900">
          <strong>Aucune inscription nécessaire.</strong> Après validation, vous pourrez revendiquer votre fiche pour la modifier vous-même.
        </div>
      </Card>

      <Card className="p-5 md:p-6 space-y-4">
        {/* Type + catégorie */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Annuaire *</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="submit-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TYPE_META).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Catégorie *</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="submit-category"><SelectValue placeholder="Choisir…" /></SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.slug} value={c.slug}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label className="text-xs">Nom professionnel / activité *</Label>
          <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Ex : Cabinet Kiné Papeete, Ori Sport Store…" data-testid="submit-name" maxLength={200} />
        </div>

        <div>
          <Label className="text-xs">Email de contact *</Label>
          <Input type="email" value={form.contact_email} onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))}
            placeholder="votre-email@example.pf" data-testid="submit-email" maxLength={200} />
          <p className="text-[10px] text-muted-foreground mt-1">
            Nous vous enverrons une confirmation à cette adresse et vous préviendrons dès la publication.
          </p>
        </div>

        <div>
          <Label className="text-xs">Description courte * (≥ 10 caractères)</Label>
          <Textarea rows={3} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="Décrivez votre activité en quelques phrases…" data-testid="submit-description" maxLength={2000} />
          <div className="text-[10px] text-muted-foreground text-right">{form.description.length} / 2000</div>
        </div>

        <div className="text-[11px] uppercase font-semibold tracking-wide text-muted-foreground pt-3 border-t">
          Coordonnées (optionnel)
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input placeholder="Île" value={form.island} onChange={(e) => setForm((f) => ({ ...f, island: e.target.value }))} data-testid="submit-island" />
          <Input placeholder="Commune" value={form.commune} onChange={(e) => setForm((f) => ({ ...f, commune: e.target.value }))} data-testid="submit-commune" />
        </div>
        <Input placeholder="Adresse complète" value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} data-testid="submit-address" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input placeholder="Téléphone" value={form.contact_phone} onChange={(e) => setForm((f) => ({ ...f, contact_phone: e.target.value }))} data-testid="submit-phone" />
          <Input placeholder="Site web (https://…)" value={form.website} onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))} data-testid="submit-website" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Input placeholder="Facebook (URL)" value={form.social_facebook} onChange={(e) => setForm((f) => ({ ...f, social_facebook: e.target.value }))} data-testid="submit-facebook" />
          <Input placeholder="Instagram (URL)" value={form.social_instagram} onChange={(e) => setForm((f) => ({ ...f, social_instagram: e.target.value }))} data-testid="submit-instagram" />
          <Input placeholder="TikTok (URL)" value={form.social_tiktok} onChange={(e) => setForm((f) => ({ ...f, social_tiktok: e.target.value }))} data-testid="submit-tiktok" />
        </div>

        {/* Honeypot invisible — les bots le remplissent, pas les humains */}
        <input
          type="text"
          name="website_confirm"
          value={form.website_confirm}
          onChange={(e) => setForm((f) => ({ ...f, website_confirm: e.target.value }))}
          style={{ position: "absolute", left: "-9999px", opacity: 0, pointerEvents: "none" }}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
        />

        <div className="pt-4 border-t flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[11px] text-muted-foreground max-w-md">
            En soumettant, vous acceptez que ces informations soient publiées après validation par AITO.PF.
            {!canSubmit && missingFields.length > 0 && (
              <div
                className="mt-1.5 flex items-start gap-1.5 text-amber-800"
                data-testid="submit-missing-fields"
              >
                <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <div className="leading-snug">
                  <span className="font-semibold">À compléter avant d'envoyer :</span>{" "}
                  <span>{missingFields.join(", ")}</span>
                </div>
              </div>
            )}
          </div>
          <Button
            onClick={submit}
            disabled={submitting}
            aria-disabled={!canSubmit}
            title={!canSubmit ? `Champs manquants : ${missingFields.join(", ")}` : "Envoyer la demande de référencement"}
            className={
              canSubmit
                ? "bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]"
                : "bg-slate-200 text-slate-500 hover:bg-slate-200 cursor-help"
            }
            data-testid="submit-button"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
            Envoyer ma demande
          </Button>
        </div>
      </Card>
    </div>
  );
}
